import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './ModePill.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The set of composer modes. `search` is the only one *Search on* draws; the union is the extension
 * point, so a second mode adds a glyph here and nothing else.
 */
export type ComposerMode = 'search';

/** Mode -> registry glyph. Same `global` asset the add menu's "Website" row uses, tinted blue. */
const MODE_GLYPH: Record<ComposerMode, string> = {
  search: 'global',
};

export interface ModePillProps {
  /** Picks the 18px glyph. `search` is the only mode drawn. */
  mode: ComposerMode;
  /** The visible name of the mode — "Search". */
  label: string;
  onClick?: () => void;
  theme?: ChatTheme;
  /** Stories and visual tests only: paints the hover wash without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * A composer mode that is currently **on**, sitting in the trailing group to the left of send.
 *
 * The caption on *Search on* is the design decision: web search is a *mode*, not a button. There is no
 * off state anywhere in the file because an off mode does not render — the toolbar used to carry a
 * permanent globe and the `+` menu swallowed it, so the bar is three affordances again and this
 * pill only appears once the mode is live. Its presence IS the state, which is why it is the whole
 * pill that is `aria-pressed`, and why the union above has no `active` boolean in it.
 *
 * Deliberately **not** `ModelPill`. Six numbers differ — padding 9/11 against 8/10, gap 6 against
 * 7, glyph 18 against 20 — and every one of them is there because a wireframe globe and a solid
 * brand mark do not occupy their boxes the same way. Building the two as one component with a
 * variant prop is exactly how those six values become three.
 */
export function ModePill({
  mode,
  label,
  onClick,
  theme: themeProp,
  forceHover = false,
  className,
}: ModePillProps) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      type="button"
      className={cn(chatThemeClass(theme), styles.pill, forceHover && styles.forceHover, className)}
      onClick={onClick}
      // The pill renders only while the mode is on, so this is the constant `true` the design
      // draws — the control is a toggle whose off state is its own absence.
      aria-pressed
    >
      <span className={styles.glyph}>
        <DeckIcon name={MODE_GLYPH[mode]} size={18} />
      </span>
      <span className={styles.label}>{label}</span>
    </button>
  );
}
